<template>
  <div class="global-search" ref="containerRef">
    <div class="global-search-input-wrap" ref="inputWrapRef">
      <svg class="global-search-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
        <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd" />
      </svg>
      <input
        ref="inputRef"
        v-model="query"
        type="text"
        class="global-search-input"
        placeholder="Search advertisers, contacts, phone, email..."
        @focus="onFocus"
        @keydown="onKeydown"
      />
      <button v-if="query" class="global-search-clear" @click="clearSearch" aria-label="Clear search">
        <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="showDropdown"
        ref="dropdownRef"
        class="global-search-dropdown"
        :style="dropdownStyle"
      >
        <div v-if="loading" class="global-search-loading">
          <span class="spinner" /> Searching...
        </div>
        <div v-else-if="error" class="global-search-empty">
          Search failed. Try again.
        </div>
        <div v-else-if="results.length === 0 && query.trim().length >= 2 && hasSearched" class="global-search-empty">
          No results found.
        </div>
        <template v-else>
          <button
            type="button"
            v-for="(item, idx) in results"
            :key="`${item.advertiserKey}-${item.matchType}-${idx}`"
            class="global-search-result"
            :class="{ highlighted: idx === highlightIndex }"
            @mousedown.prevent="selectResult(item)"
            @mouseenter="highlightIndex = idx"
          >
            <div class="search-result-main">
              <span class="search-result-name">{{ item.advertiserName }}</span>
              <span class="search-result-badge" :class="badgeClass(item.matchType)">{{ item.matchTypeDisplay }}</span>
            </div>
            <div v-if="item.contactName || item.matchedValue !== item.advertiserName" class="search-result-detail">
              <span v-if="item.contactName" class="search-result-contact">{{ item.contactName }} &mdash; </span>
              <span class="search-result-value">{{ item.matchedValue }}</span>
            </div>
          </button>
          <button
            v-if="hasMore"
            type="button"
            class="global-search-load-more"
            :disabled="loadingMore"
            @mousedown.prevent="loadMore"
          >{{ loadingMore ? 'Loading...' : 'Load more results' }}</button>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { companyPath } from '@/utils/companyNavigation'
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { globalSearch } from '@/api/salesRepApi'
import type { SearchResultItem, SearchMatchType } from '@/types'

const router = useRouter()
const containerRef = ref<HTMLElement | null>(null)
const inputWrapRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)

const query = ref('')
const results = ref<SearchResultItem[]>([])
const loading = ref(false)
const error = ref(false)
const hasSearched = ref(false)
const showDropdown = ref(false)
const highlightIndex = ref(-1)
const hasMore = ref(false)
const loadingMore = ref(false)

// Position the dropdown below the input using fixed positioning (teleported to body)
const dropdownPos = ref({ top: 0, left: 0, width: 0 })

function updateDropdownPos() {
  if (!inputWrapRef.value) return
  const rect = inputWrapRef.value.getBoundingClientRect()
  dropdownPos.value = {
    top: rect.bottom + 6,
    left: rect.left,
    width: Math.max(rect.width, 340)
  }
}

const dropdownStyle = computed(() => ({
  position: 'fixed' as const,
  top: `${dropdownPos.value.top}px`,
  left: `${dropdownPos.value.left}px`,
  width: `${dropdownPos.value.width}px`,
  zIndex: 9999
}))

let debounceTimer: ReturnType<typeof setTimeout> | null = null

function badgeClass(matchType: SearchMatchType): string {
  switch (matchType) {
    case 'ContactName': return 'badge-match-name'
    case 'ContactPhone':
    case 'AdvertiserPhone': return 'badge-match-phone'
    case 'ContactEmail':
    case 'AdvertiserEmail': return 'badge-match-email'
    case 'AdvertiserName':
    case 'AdvertiserAddressee': return 'badge-match-name'
    default: return ''
  }
}

watch(query, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  const trimmed = val.trim()
  if (trimmed.length < 2) {
    results.value = []
    hasSearched.value = false
    error.value = false
    if (trimmed.length === 0) showDropdown.value = false
    return
  }
  loading.value = true
  error.value = false
  showDropdown.value = true
  updateDropdownPos()
  debounceTimer = setTimeout(async () => {
    try {
      const response = await globalSearch(trimmed)
      results.value = response.items
      hasMore.value = response.hasMore
      hasSearched.value = true
      error.value = false
    } catch {
      error.value = true
      results.value = []
      hasMore.value = false
    } finally {
      loading.value = false
      highlightIndex.value = -1
    }
  }, 300)
})

function onFocus() {
  if (query.value.trim().length >= 2) {
    showDropdown.value = true
    updateDropdownPos()
  }
}

function onKeydown(e: KeyboardEvent) {
  if (!showDropdown.value || results.value.length === 0) {
    if (e.key === 'Escape') {
      showDropdown.value = false
      inputRef.value?.blur()
    }
    return
  }

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      highlightIndex.value = Math.min(highlightIndex.value + 1, results.value.length - 1)
      scrollHighlightedIntoView()
      break
    case 'ArrowUp':
      e.preventDefault()
      highlightIndex.value = Math.max(highlightIndex.value - 1, 0)
      scrollHighlightedIntoView()
      break
    case 'Enter':
      e.preventDefault()
      if (highlightIndex.value >= 0 && highlightIndex.value < results.value.length) {
        selectResult(results.value[highlightIndex.value])
      }
      break
    case 'Escape':
      showDropdown.value = false
      inputRef.value?.blur()
      break
  }
}

function scrollHighlightedIntoView() {
  nextTick(() => {
    const el = dropdownRef.value?.querySelector('.global-search-result.highlighted')
    if (el) el.scrollIntoView({ block: 'nearest' })
  })
}

function selectResult(item: SearchResultItem) {
  showDropdown.value = false
  query.value = ''
  results.value = []
  hasSearched.value = false

  const isContactResult = item.matchType === 'ContactName' || item.matchType === 'ContactPhone' || item.matchType === 'ContactEmail'
  if (isContactResult) {
    router.push({
      path: companyPath(`/accounts/${item.advertiserKey}`),
      query: {
        tab: 'contacts',
        focusContact: '1',
        focusContactKey: item.contactKey != null ? String(item.contactKey) : undefined,
        contactName: item.contactName ?? undefined,
        matchedValue: item.matchedValue || undefined,
        matchType: item.matchType
      }
    })
    return
  }

  router.push(companyPath(`/accounts/${item.advertiserKey}`))
}

function clearSearch() {
  query.value = ''
  results.value = []
  hasSearched.value = false
  showDropdown.value = false
  error.value = false
  hasMore.value = false
  inputRef.value?.focus()
}

async function loadMore() {
  loadingMore.value = true
  try {
    const response = await globalSearch(query.value.trim(), results.value.length)
    results.value = [...results.value, ...response.items]
    hasMore.value = response.hasMore
  } catch { /* ignore */ }
  finally { loadingMore.value = false }
}

function onClickOutside(e: MouseEvent) {
  const target = e.target as Node
  if (containerRef.value && containerRef.value.contains(target)) return
  if (dropdownRef.value && dropdownRef.value.contains(target)) return
  showDropdown.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', onClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onClickOutside)
  if (debounceTimer) clearTimeout(debounceTimer)
})
</script>

<style scoped>
.global-search {
  position: relative;
  flex: 0 1 340px;
  min-width: 0;
}

.global-search-input-wrap {
  display: flex;
  align-items: center;
  background: rgba(255,255,255,0.12);
  border-radius: 0.375rem;
  padding: 0 0.625rem;
  gap: 0.375rem;
  transition: background 0.15s;
}

.global-search-input-wrap:focus-within {
  background: rgba(255,255,255,0.2);
}

.global-search-icon {
  color: rgba(255,255,255,0.6);
  flex-shrink: 0;
}

.global-search-input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: var(--nav-text);
  font-size: 0.8125rem;
  padding: 0.4375rem 0;
  min-width: 0;
}

.global-search-input::placeholder {
  color: rgba(255,255,255,0.5);
}

.global-search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: rgba(255,255,255,0.6);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
}

.global-search-clear:hover {
  color: #fff;
  background: rgba(255,255,255,0.15);
}

/* Mobile */
@media (max-width: 768px) {
  .global-search {
    flex: 1 1 100%;
    order: 5;
  }
}
</style>

<style>
/* Dropdown styles — unscoped because it's teleported to body */
.global-search-dropdown {
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-lg);
  max-height: 400px;
  overflow-y: auto;
}

.global-search-loading,
.global-search-empty {
  padding: 1rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.global-search-result {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.625rem 0.875rem;
  background: none;
  border: none;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  font-size: 0.8125rem;
  color: var(--text-primary);
  transition: background 0.1s;
}

.global-search-result:last-child {
  border-bottom: none;
}

.global-search-result:hover,
.global-search-result.highlighted {
  background: var(--bg-hover);
}

.search-result-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.search-result-name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-result-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.0625rem 0.375rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.badge-match-name {
  background: var(--accent-100);
  color: var(--accent-700);
}

.badge-match-phone {
  background: var(--success-50);
  color: var(--success-700);
}

.badge-match-email {
  background: var(--warning-50);
  color: var(--warning-700);
}

.search-result-detail {
  margin-top: 0.125rem;
  color: var(--text-muted);
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-result-contact {
  color: var(--text-secondary);
}

.global-search-load-more {
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-top: 1px solid var(--border-color);
  background: var(--bg-tertiary);
  color: var(--accent-primary);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
}

.global-search-load-more:hover:not(:disabled) {
  background: var(--bg-hover);
}

.global-search-load-more:disabled {
  opacity: 0.6;
  cursor: wait;
}
</style>
